import React, { useState, useRef, useEffect } from 'react';
import { Video, Palette, Upload, Loader, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ComponentType } from '../../types/builder';
import { useBuilderStore } from '../../store/builderStore';
import { createDefaultComponent } from '../../utils/componentDefaults';
import toast from 'react-hot-toast';

/**
 * Curated component groups for the Builder sidebar.
 *
 * Each component is opt-in here — if a `ComponentType` is NOT listed
 * in any group, it simply won't appear in the picker. The renderer,
 * settings panel, type definitions and default factory all still
 * support every legacy component, so any landing page that already
 * has e.g. `timeline` or `textBlock` keeps rendering correctly. We
 * just don't let users ADD more of them.
 *
 * Removed from the picker (still rendered for legacy pages):
 *   • textBlock        — superseded by `bio` (rich text)
 *   • linkCard         — superseded by `link` (with optional thumbnail)
 *   • gameCarousel     — superseded by `bannerSlide`
 *   • button, ctaButton — handled inline by other components
 *                         (forms, link cards, banner CTAs)
 *   • divider, spacer   — spacing handled by component padding/margins
 *   • timeline, locationMap, giftRegistry, addToCalendar,
 *     photoGallery, loveStory, guestbook, dressCode
 *                      — wedding/event-specific, off-scope for the
 *                        current builder positioning
 */
// Each group has a stable `id` (used for i18n key lookup under
// `componentSidebar.groups.<id>.label` and `…items.<itemKey>`).
// `itemKey` defaults to the component `type` but we keep it explicit
// so the label key is decoupled from any future internal rename of
// `ComponentType`.
type ComponentItem = { type: ComponentType; itemKey: string; emoji: string };
type ComponentGroup = { id: string; items: ComponentItem[] };

const componentGroups: ComponentGroup[] = [
  {
    id: 'identity',
    items: [
      { type: 'profile',   itemKey: 'profile',   emoji: '👤' },
      { type: 'name',      itemKey: 'name',      emoji: '✍️' },
      { type: 'bio',       itemKey: 'bio',       emoji: '📝' },
      { type: 'brandLogo', itemKey: 'brandLogo', emoji: '🏷️' },
    ],
  },
  {
    id: 'content',
    items: [
      { type: 'picture',       itemKey: 'picture',       emoji: '🖼️' },
      { type: 'pictureSlider', itemKey: 'pictureSlider', emoji: '🎠' },
      { type: 'video',         itemKey: 'video',         emoji: '🎬' },
      { type: 'bannerSlide',   itemKey: 'bannerSlide',   emoji: '🎪' },
    ],
  },
  {
    id: 'action',
    items: [
      { type: 'link', itemKey: 'link', emoji: '🔗' },
    ],
  },
  {
    id: 'engage',
    items: [
      { type: 'form',        itemKey: 'form',        emoji: '📋' },
      { type: 'survey',      itemKey: 'survey',      emoji: '📊' },
      { type: 'faq',         itemKey: 'faq',         emoji: '❓' },
      { type: 'socialIcons', itemKey: 'socialIcons', emoji: '📱' },
    ],
  },
  {
    // Note: i18n group id uses camelCase (`socialProof`); the kebab-case
    // `social-proof` legacy id is kept out of JSON to keep keys uniform.
    id: 'socialProof',
    items: [
      { type: 'testimonial',        itemKey: 'testimonial',        emoji: '⭐' },
      // Differentiated from Testimonial ⭐ by switching to 🌟.
      { type: 'googleReviews',      itemKey: 'googleReviews',      emoji: '🌟' },
      { type: 'onlineUsers',        itemKey: 'onlineUsers',        emoji: '👥' },
      { type: 'recentWithdrawals',  itemKey: 'recentWithdrawals',  emoji: '💸' },
      { type: 'statsBox',           itemKey: 'statsBox',           emoji: '🎁' },
      { type: 'leaderboard',        itemKey: 'leaderboard',        emoji: '🏆' },
      { type: 'partnerLogos',       itemKey: 'partnerLogos',       emoji: '🤝' },
    ],
  },
  {
    id: 'interactive',
    items: [
      { type: 'gameRTP',        itemKey: 'gameRTP',        emoji: '📈' },
      // Was 🏆 (collided with Leaderboard); switched to 💰 so the
      // ranking widget and the prize-amount widget read at a glance.
      { type: 'jackpot',        itemKey: 'jackpot',        emoji: '💰' },
      { type: 'countdownTimer', itemKey: 'countdownTimer', emoji: '⏰' },
      { type: 'spinWheel',      itemKey: 'spinWheel',      emoji: '🎰' },
      { type: 'musicPlayer',    itemKey: 'musicPlayer',    emoji: '🎵' },
    ],
  },
];

const solidColors = [
  '#ffffff', '#f5f5f5', '#e0e0e0', '#1a1a2e', '#16213e', 
  '#0f1419', '#FFD700', '#FF6B35', '#00ff88', '#3b82f6',
  '#8b5cf6', '#ec4899', '#f43f5e', '#10b981', '#f59e0b'
];

// 🎨 Extended Gradient Collection (55+ Beautiful Gradients)
const gradientColors = [
  // 🌅 Warm & Sunset Gradients
  'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', // Sunset
  'linear-gradient(135deg, #f12711 0%, #f5af19 100%)', // Fire
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', // Peach
  'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)', // Orange Coral
  'linear-gradient(135deg, #ffe259 0%, #ffa751 100%)', // Mango
  'linear-gradient(135deg, #f6d365 0%, #fda085 100%)', // Sunny
  'linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)', // Citrus
  
  // 🌊 Cool & Ocean Gradients
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Ocean
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Blue Lagoon
  'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)', // Arctic
  'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)', // Cool Sky
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Deep Blue
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // Deep Ocean
  'linear-gradient(135deg, #0093E9 0%, #80D0C7 100%)', // Aqua
  
  // 🌸 Pink & Purple Gradients
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // Purple Dream
  'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)', // Pink Passion
  'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)', // Lavender
  'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)', // Cotton Candy
  'linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)', // Velvet
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink Neon
  'linear-gradient(135deg, #c471f5 0%, #fa71cd 100%)', // Purple Pink
  
  // 🌿 Green & Nature Gradients
  'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)', // Forest
  'linear-gradient(135deg, #d9ead3 0%, #a7e9af 100%)', // Mint
  'linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)', // Lime
  'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)', // Emerald
  'linear-gradient(135deg, #134e5e 0%, #71b280 100%)', // Jungle
  
  // 🌈 Rainbow & Vibrant Gradients
  'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)', // Rainbow
  'linear-gradient(135deg, #fa8bff 0%, #2bd2ff 52%, #2bff88 100%)', // Neon
  'linear-gradient(135deg, #08aeea 0%, #2af598 100%)', // Electric
  'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)', // Cosmic
  'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', // Aurora
  'linear-gradient(135deg, #3b41c5 0%, #a981bb 50%, #ffc8a2 100%)', // Vibrant
  
  // 🌑 Dark & Elegant Gradients
  'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)', // Dark Night
  'linear-gradient(135deg, #f4c4f3 0%, #fc67fa 100%)', // Black Rose
  'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)', // Deep Purple
  'linear-gradient(135deg, #232526 0%, #414345 100%)', // Midnight
  'linear-gradient(135deg, #536976 0%, #292e49 100%)', // Graphite
  
  // ☀️ Bright & Yellow Gradients
  'linear-gradient(135deg, #fff720 0%, #3cd500 100%)', // Lemon
  'linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)', // Golden
  'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)', // Yellow Bliss
  
  // 🎨 Pastel Gradients
  'linear-gradient(135deg, #fff1eb 0%, #ace0f9 100%)', // Pastel Rainbow
  'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)', // Cream
  'linear-gradient(135deg, #ddd6f3 0%, #faaca8 100%)', // Lavender Mist
  
  // 💎 Luxury & Premium Gradients
  'linear-gradient(135deg, #141E30 0%, #243B55 100%)', // Royal
  'linear-gradient(135deg, #c31432 0%, #240b36 100%)', // Ruby
  'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)', // Sapphire
  'linear-gradient(135deg, #bdc3c7 0%, #2c3e50 100%)', // Platinum
  'linear-gradient(135deg, #d4af37 0%, #f9d423 100%)', // Gold
  
  // 🌺 Tropical & Exotic Gradients
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Tropical
  'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)', // Hawaii
  'linear-gradient(135deg, #22c1c3 0%, #fdbb2d 100%)', // Caribbean
  
  // 🏙️ Modern & Urban Gradients
  'linear-gradient(135deg, #f77062 0%, #fe5196 100%)', // Cyberpunk
  'linear-gradient(135deg, #868f96 0%, #596164 100%)', // Steel
  'linear-gradient(135deg, #485563 0%, #29323c 100%)', // Industrial
  'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)', // Metro
];

interface ComponentSidebarProps {
  /** Mobile drawer open state — at lg+ the sidebar is in-flow so
   *  this only affects screens below the breakpoint. */
  isMobileOpen?: boolean;
  /** Called when the user dismisses the mobile drawer (close button,
   *  backdrop tap, or after picking a component). */
  onMobileClose?: () => void;
}

export const ComponentSidebar: React.FC<ComponentSidebarProps> = ({
  isMobileOpen = false,
  onMobileClose,
}) => {
  const { t } = useTranslation('builder');
  const { currentPage, addComponent, updatePageSettings } = useBuilderStore();
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const [activeTab, setActiveTab] = useState<'solid' | 'gradient'>('solid');
  const bgImageInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingBgImage, setIsUploadingBgImage] = useState(false);
  const bgVideoInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingBgVideo, setIsUploadingBgVideo] = useState(false);

  // ✅ Auto-detect และ switch tab เมื่อ backgroundColor เป็น gradient
  useEffect(() => {
    const bgColor = currentPage?.settings.backgroundColor;
    if (typeof bgColor === 'string' && bgColor.startsWith('linear-gradient')) {
      setActiveTab('gradient');
    } else if (typeof bgColor === 'string' && bgColor.startsWith('#')) {
      setActiveTab('solid');
    }
  }, [currentPage?.settings.backgroundColor]);

  const handleAddComponent = (type: ComponentType) => {
    const position = currentPage?.components.length || 0;
    const newComponent = createDefaultComponent(type, position);
    addComponent(newComponent);
    // On mobile, close the picker so the user immediately sees their
    // newly-inserted component land on the canvas. We don't need a
    // breakpoint check — onMobileClose is a no-op on desktop because
    // we never pass it from the always-visible lg layout.
    onMobileClose?.();
  };

  const handleBgImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('componentSidebar.background.toast.imageInvalidType'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('componentSidebar.background.toast.imageTooLarge'));
      return;
    }

    setIsUploadingBgImage(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('componentSidebar.background.toast.uploadFailed'));
      }

      const data = await response.json();
      updatePageSettings({ backgroundImage: data.url });
      setActiveTab('solid');
      toast.success(t('componentSidebar.background.toast.imageUploadSuccess'));
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || t('componentSidebar.background.toast.imageUploadFailed'));
    } finally {
      setIsUploadingBgImage(false);
      // Reset input
      if (bgImageInputRef.current) {
        bgImageInputRef.current.value = '';
      }
    }
  };

  const handleBgVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error(t('componentSidebar.background.toast.videoInvalidType'));
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error(t('componentSidebar.background.toast.videoTooLarge'));
      return;
    }

    setIsUploadingBgVideo(true);

    try {
      const formData = new FormData();
      formData.append('video', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('componentSidebar.background.toast.uploadFailed'));
      }

      const data = await response.json();
      updatePageSettings({ backgroundVideo: data.url });
      toast.success(t('componentSidebar.background.toast.videoUploadSuccess'));
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || t('componentSidebar.background.toast.videoUploadFailed'));
    } finally {
      setIsUploadingBgVideo(false);
      // Reset input
      if (bgVideoInputRef.current) {
        bgVideoInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      {/* Mobile-only backdrop. Visible below the lg breakpoint when the
          drawer is open — desktop layout never mounts this because
          `isMobileOpen` stays false there. */}
      {isMobileOpen && (
        <button
          type="button"
          aria-label="Close components panel"
          onClick={onMobileClose}
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        />
      )}

      <div
        className={`
          theme-fixed fixed inset-y-0 left-0 z-50 w-[88vw] max-w-sm bg-gray-900 text-white overflow-y-auto shadow-2xl border-r border-gray-800 transform transition-transform duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:shadow-none lg:border-r-0 lg:z-auto lg:w-72 lg:max-w-none lg:transition-none
        `}
      >
      {/* Mobile-only header with close button */}
      <div className="lg:hidden sticky top-0 z-10 flex items-center justify-between bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 px-4 py-3">
        <span className="text-sm font-semibold text-white">{t('componentSidebar.background.open')}</span>
        <button
          type="button"
          onClick={onMobileClose}
          className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="p-4 sm:p-6">
      {/* Background Color Picker Section */}
      <div className="mb-6">
        <button
          onClick={() => setShowBackgroundPicker(!showBackgroundPicker)}
          className="w-full flex items-center gap-3 p-3 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors duration-200"
        >
          <Palette size={20} />
          <span className="text-sm font-semibold">{t('componentSidebar.background.open')}</span>
        </button>

        {showBackgroundPicker && (
          <div className="mt-3 bg-gray-800 rounded-lg p-4 space-y-4">
            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('solid')}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeTab === 'solid'
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-900 text-gray-400 hover:text-white'
                }`}
              >
                {t('componentSidebar.background.tabs.solid')}
              </button>
              <button
                onClick={() => setActiveTab('gradient')}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeTab === 'gradient'
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-900 text-gray-400 hover:text-white'
                }`}
              >
                {t('componentSidebar.background.tabs.gradient')}
              </button>
            </div>

            {/* Background Image */}
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                {t('componentSidebar.background.image.label')}
              </label>
              
              {/* Upload button */}
              <div className="mb-2">
                <input
                  ref={bgImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBgImageUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => bgImageInputRef.current?.click()}
                  disabled={isUploadingBgImage}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  {isUploadingBgImage ? (
                    <>
                      <Loader className="w-3 h-3 animate-spin" />
                      {t('componentSidebar.background.image.uploading')}
                    </>
                  ) : (
                    <>
                      <Upload className="w-3 h-3" />
                      {t('componentSidebar.background.image.uploadCta')}
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  {t('componentSidebar.background.image.hint')}
                </p>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t('componentSidebar.background.image.orUrl')}
                </label>
                <input
                  type="text"
                  value={currentPage?.settings.backgroundImage || ''}
                  onChange={(e) => {
                    updatePageSettings({ backgroundImage: e.target.value });
                    if (e.target.value.trim()) {
                      setActiveTab('solid');
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('componentSidebar.background.image.urlPlaceholder')}
                />
              </div>

              {/* Image Preview & Clear Button */}
              {currentPage?.settings.backgroundImage && (
                <div className="mt-3">
                  <div className="relative">
                    <img
                      src={currentPage.settings.backgroundImage}
                      alt="Background preview"
                      className="w-full h-32 object-cover rounded-lg border border-gray-700"
                    />
                    <button
                      type="button"
                      onClick={() => updatePageSettings({ backgroundImage: '' })}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                      title={t('componentSidebar.background.image.remove')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-green-400 mt-2">
                    {t('componentSidebar.background.image.active')}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                {t('componentSidebar.background.video.label')}
              </label>
              
              {/* Upload button */}
              <div className="mb-2">
                <input
                  ref={bgVideoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleBgVideoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => bgVideoInputRef.current?.click()}
                  disabled={isUploadingBgVideo}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  {isUploadingBgVideo ? (
                    <>
                      <Loader className="w-3 h-3 animate-spin" />
                      {t('componentSidebar.background.video.uploading')}
                    </>
                  ) : (
                    <>
                      <Video className="w-3 h-3" />
                      {t('componentSidebar.background.video.uploadCta')}
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  {t('componentSidebar.background.video.hint')}
                </p>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t('componentSidebar.background.video.orUrl')}
                </label>
                <input
                  type="text"
                  value={currentPage?.settings.backgroundVideo || ''}
                  onChange={(e) => {
                    updatePageSettings({ backgroundVideo: e.target.value });
                  }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t('componentSidebar.background.video.urlPlaceholder')}
                />
              </div>

              {/* Video Preview & Clear Button */}
              {currentPage?.settings.backgroundVideo && (
                <div className="mt-3">
                  <div className="relative">
                    <video
                      src={currentPage.settings.backgroundVideo}
                      className="w-full h-32 object-cover rounded-lg border border-gray-700"
                      muted
                      loop
                      autoPlay
                    />
                    <button
                      type="button"
                      onClick={() => updatePageSettings({ backgroundVideo: '' })}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                      title={t('componentSidebar.background.video.remove')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-purple-400 mt-2">
                    {t('componentSidebar.background.video.active')}
                  </p>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-300">
                  {activeTab === 'solid'
                    ? t('componentSidebar.background.color.pickLabel')
                    : t('componentSidebar.background.color.customCssLabel')}
                </label>
                {currentPage?.settings.backgroundColor && (
                  <button
                    onClick={() => updatePageSettings({ backgroundColor: '' })}
                    className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
                    title={t('componentSidebar.background.color.clearTitle')}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t('componentSidebar.background.color.clear')}
                  </button>
                )}
              </div>
              {activeTab === 'solid' ? (
                <input
                  type="color"
                  value={
                    (typeof currentPage?.settings.backgroundColor === 'string' && currentPage.settings.backgroundColor.startsWith('#'))
                      ? currentPage.settings.backgroundColor 
                      : '#ffffff'
                  }
                  onChange={(e) => updatePageSettings({ backgroundColor: e.target.value })}
                  className="w-full h-10 rounded-lg cursor-pointer"
                />
              ) : (
                <input
                  type="text"
                  value={currentPage?.settings.backgroundColor || ''}
                  onChange={(e) => updatePageSettings({ backgroundColor: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('componentSidebar.background.color.customCssPlaceholder')}
                />
              )}
            </div>

            {activeTab === 'solid' && (
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-2">
                  {t('componentSidebar.background.color.hexLabel')}
                </label>
                <input
                  type="text"
                  value={
                    (typeof currentPage?.settings.backgroundColor === 'string' && currentPage.settings.backgroundColor.startsWith('#'))
                      ? currentPage.settings.backgroundColor 
                      : '#ffffff'
                  }
                  onChange={(e) => updatePageSettings({ backgroundColor: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('componentSidebar.background.color.hexPlaceholder')}
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2 flex items-center justify-between">
                <span>{t('componentSidebar.background.presets.label')}</span>
                <span className="text-gray-500 text-[10px]">
                  {activeTab === 'solid'
                    ? t('componentSidebar.background.presets.colorsCount', { count: solidColors.length })
                    : t('componentSidebar.background.presets.gradientsCount', { count: gradientColors.length })}
                </span>
              </label>
              <div className="max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                <div className="grid grid-cols-5 gap-2">
                  {(activeTab === 'solid' ? solidColors : gradientColors).map((color, index) => (
                    <button
                      key={index}
                      onClick={() => updatePageSettings({ backgroundColor: color })}
                      className="w-full h-10 rounded-lg border-2 border-gray-700 hover:border-white hover:scale-105 transition-all flex-shrink-0"
                      style={{ background: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <h2 className="text-lg font-semibold mb-4">{t('componentSidebar.componentsHeading')}</h2>

      <div className="space-y-5">
        {componentGroups.map((group) => (
          <div key={group.id}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2 px-1">
              {t(`componentSidebar.groups.${group.id}.label` as any)}
            </div>
            <div className="space-y-2">
              {group.items.map((component) => (
                <button
                  key={component.type}
                  onClick={() => handleAddComponent(component.type)}
                  className="w-full flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors duration-200 text-left"
                >
                  <span className="text-2xl">{component.emoji}</span>
                  <span className="text-sm font-medium">
                    {t(`componentSidebar.groups.${group.id}.items.${component.itemKey}` as any)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      </div>
      </div>
    </>
  );
};

